import { z } from "zod";

/**
 * Rich text content is stored as a constrained subset of Tiptap/ProseMirror
 * JSON. Only the node and mark types listed here are legal — the editor's
 * paste sanitisation and the renderer's serializer both enforce this set.
 */

export const RichMarkSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("bold") }).strict(),
    z.object({ type: z.literal("italic") }).strict(),
    z.object({ type: z.literal("underline") }).strict(),
    z.object({ type: z.literal("strike") }).strict(),
    z
        .object({
            type: z.literal("link"),
            attrs: z.object({ href: z.string().max(2000) }).strict(),
        })
        .strict(),
    z
        .object({
            type: z.literal("textStyle"),
            attrs: z.object({ color: z.string().regex(/^#[0-9a-fA-F]{3,8}$/) }).strict(),
        })
        .strict(),
]);
export type RichMark = z.infer<typeof RichMarkSchema>;

export interface RichTextNode {
    type: "text";
    text: string;
    marks?: RichMark[];
}

export interface RichMergeTagNode {
    type: "mergeTag";
    attrs: { tag: string; fallback?: string };
}

export interface RichHardBreakNode {
    type: "hardBreak";
}

export type RichInlineNode = RichTextNode | RichMergeTagNode | RichHardBreakNode;

export interface RichParagraphNode {
    type: "paragraph";
    attrs?: { textAlign?: "left" | "center" | "right" | "justify" };
    content?: RichInlineNode[];
}

export interface RichListItemNode {
    type: "listItem";
    content: RichParagraphNode[];
}

export interface RichListNode {
    type: "bulletList" | "orderedList";
    content: RichListItemNode[];
}

export type RichBlockNode = RichParagraphNode | RichListNode;

export interface RichDoc {
    type: "doc";
    content: RichBlockNode[];
}

const RichInlineSchema: z.ZodType<RichInlineNode> = z.union([
    z
        .object({
            type: z.literal("text"),
            text: z.string(),
            marks: z.array(RichMarkSchema).optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal("mergeTag"),
            attrs: z
                .object({
                    tag: z.string().regex(/^[a-zA-Z0-9_.]+$/),
                    fallback: z.string().max(200).optional(),
                })
                .strict(),
        })
        .strict(),
    z.object({ type: z.literal("hardBreak") }).strict(),
]);

const RichParagraphSchema: z.ZodType<RichParagraphNode> = z
    .object({
        type: z.literal("paragraph"),
        attrs: z
            .object({
                textAlign: z.enum(["left", "center", "right", "justify"]).optional(),
            })
            .strict()
            .optional(),
        content: z.array(RichInlineSchema).optional(),
    })
    .strict();

const RichListItemSchema: z.ZodType<RichListItemNode> = z
    .object({
        type: z.literal("listItem"),
        content: z.array(RichParagraphSchema),
    })
    .strict();

const RichListSchema: z.ZodType<RichListNode> = z
    .object({
        type: z.enum(["bulletList", "orderedList"]),
        content: z.array(RichListItemSchema),
    })
    .strict();

export const RichDocSchema: z.ZodType<RichDoc> = z
    .object({
        type: z.literal("doc"),
        content: z.array(z.union([RichParagraphSchema, RichListSchema])),
    })
    .strict();

export function richTextFromPlain(text: string): RichDoc {
    return {
        type: "doc",
        content: [{ type: "paragraph", content: text ? [{ type: "text", text }] : [] }],
    };
}

/** Concatenated plain text of a rich doc (merge tags kept as tokens). */
export function richTextToPlain(
    doc: RichDoc,
    opts?: { includeLinkUrls?: boolean },
): string {
    const lines: string[] = [];
    const inline = (nodes: RichInlineNode[] | undefined): string =>
        (nodes ?? [])
            .map((n) => {
                if (n.type === "text") {
                    const link = opts?.includeLinkUrls
                        ? n.marks?.find((m) => m.type === "link")
                        : undefined;
                    if (link && link.type === "link" && link.attrs.href) {
                        return `${n.text} (${link.attrs.href})`;
                    }
                    return n.text;
                }
                if (n.type === "mergeTag")
                    return n.attrs.fallback !== undefined
                        ? `{{${n.attrs.tag}|${n.attrs.fallback}}}`
                        : `{{${n.attrs.tag}}}`;
                return "\n";
            })
            .join("");
    for (const block of doc.content) {
        if (block.type === "paragraph") {
            lines.push(inline(block.content));
        } else {
            block.content.forEach((item, i) => {
                const marker = block.type === "orderedList" ? `${i + 1}. ` : "- ";
                lines.push(marker + item.content.map((p) => inline(p.content)).join(" "));
            });
        }
    }
    return lines.join("\n");
}
